
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCreateCompany, CreateCompanyData } from "@/hooks/use-companies";
import { useStaff } from "@/hooks/use-staff";
import { usePartners } from "@/hooks/use-partners";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AvatarUpload } from "./AvatarUpload";
import { Building2 } from "lucide-react";
import { INDUSTRY_OPTIONS } from "@/constants/organization";

const companySchema = z.object({
  name: z.string().min(1, "Company name is required"),
  website: z.string().optional(),
  industry: z.string().optional(),
  address: z.string().optional(),
  country: z.string().optional(),
  division: z.string().optional(),
  notes: z.string().optional(),
  logo_url: z.string().optional(),
  description: z.string().optional(),
  opening_times: z.string().optional(),
  marketing_banner_url: z.string().optional(),
  image_1_url: z.string().optional(),
  image_2_url: z.string().optional(),
  image_3_url: z.string().optional(),
  google_places_id: z.string().optional(),
  internal_owner_id: z.string().optional(),
  external_owner_id: z.string().optional(),
});

export const CompanyModal = () => {
  const [open, setOpen] = useState(false);
  const createCompany = useCreateCompany({ syncToBuildhip: true });
  const { data: staff = [] } = useStaff();
  const { data: partners = [] } = usePartners();

  const form = useForm<CreateCompanyData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: "",
      website: "",
      industry: "",
      address: "",
      country: "",
      division: "",
      notes: "",
      logo_url: "",
      description: "",
      opening_times: "",
      marketing_banner_url: "",
      image_1_url: "",
      image_2_url: "",
      image_3_url: "",
      google_places_id: "",
      internal_owner_id: undefined,
      external_owner_id: undefined,
    },
  });

  const onSubmit = async (data: CreateCompanyData) => {
    createCompany.mutate(data, {
      onSuccess: () => {
        setOpen(false);
        form.reset();
      },
    });
  };

  const getInitials = (name: string) => {
    if (!name) return "ORG";
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[hsl(60,73%,76%)] text-black border border-gray-300 hover:bg-[hsl(60,73%,70%)]">
          <Building2 className="h-4 w-4 mr-2" />
          Add Organization
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add New Organization</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="basic" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic" className="data-[state=active]:bg-lime-button data-[state=active]:text-black">Basic Information</TabsTrigger>
            <TabsTrigger value="branding">Branding & Details</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="flex-1 overflow-auto">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-6">
                <div className="space-y-2">
                  <FormLabel className="text-slate-800 font-medium">Organization Logo</FormLabel>
                  <AvatarUpload
                    currentImageUrl={form.watch("logo_url")}
                    onImageChange={(imageUrl) => form.setValue("logo_url", imageUrl)}
                    fallbackText={getInitials(form.watch("name"))}
                    size="lg"
                  />
                </div>

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization Name *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter organization name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                   <FormField
                    control={form.control}
                    name="industry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Industry</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select industry" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {INDUSTRY_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter country" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Enter full address" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="division"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Division</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Business division or department" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Organization description" className="min-h-[80px]" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="internal_owner_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Internal Owner (Staff)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select staff member" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">No staff owner</SelectItem>
                            {staff.map((member) => (
                              <SelectItem key={member.id} value={member.id}>
                                {member.first_name} {member.last_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="external_owner_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>External Owner (Partner)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select partner" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">No partner owner</SelectItem>
                            {partners.map((partner) => (
                              <SelectItem key={partner.id} value={partner.id}>
                                {partner.first_name} {partner.last_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Internal Notes</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Internal notes about the organization (not visible to customers)" className="min-h-[80px]" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="branding" className="flex-1 overflow-auto">
            <Form {...form}>
              <div className="space-y-4 p-6">
                <h3 className="text-lg font-semibold">Branding & Marketing</h3>
                <p className="text-sm text-muted-foreground">
                  Set up visual branding elements and marketing materials for this organization.
                </p>

                <FormField
                  control={form.control}
                  name="opening_times"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Opening Times</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="e.g., Mon-Fri: 9:00-17:00, Sat: 10:00-14:00" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="marketing_banner_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Marketing Banner URL</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="https://example.com/banner.jpg" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="image_1_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Image 1 URL</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com/image1.jpg" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="image_2_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Image 2 URL</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com/image2.jpg" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="image_3_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Image 3 URL</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com/image3.jpg" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="google_places_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Google Places ID</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Form>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-2 p-6 pt-0">
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={form.handleSubmit(onSubmit)} 
            disabled={createCompany.isPending}
            className="bg-[hsl(60,73%,76%)] text-black hover:bg-[hsl(60,73%,70%)]"
          >
            {createCompany.isPending ? "Creating..." : "Create Organization"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
